import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, Users, Check, AlertCircle, PieChart, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

// Tooltip wrapper component for consistent styling
const FieldTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <button type="button" className="ml-2 text-amber-400 hover:text-amber-300 transition-colors inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/50">
          <Info size={12} strokeWidth={2.5} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-900 border-amber-500/50 text-slate-100 text-sm p-3 shadow-lg shadow-amber-500/10">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const Step5Beneficiaries = ({ data, onNext, onPrevious, onSaveAndExit, saving }) => {
  const [formData, setFormData] = useState({
    beneficiaries: data.beneficiaries || [],
    distribution_method: data.distribution_method || 'percentage',
    asset_allocations: data.asset_allocations || {} // { asset_id: { beneficiary_index: percentage } }
  });

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const selectClass = "bg-slate-800 border-slate-700 text-white w-full p-2.5 rounded-md focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 outline-none";
  const labelClass = "text-slate-300 font-medium text-sm";

  // Auto-add first beneficiary if none exist
  useEffect(() => {
    if (formData.beneficiaries.length === 0) {
      setFormData(prev => ({
        ...prev,
        beneficiaries: [{
          name: '',
          relationship: '',
          share_percentage: '',
          contingent_beneficiary: ''
        }]
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get all assets from previous steps
  const getAllAssets = () => {
    const assets = [];
    
    // Immovable Assets
    if (data.immovable_assets) {
      data.immovable_assets.forEach((asset, idx) => {
        assets.push({
          id: `immovable_${idx}`,
          category: 'Immovable Property',
          name: `${asset.type || 'Property'} - ${asset.address || 'No address'}`,
          type: 'immovable'
        });
      });
    }

    // Financial Assets
    if (data.financial_assets) {
      if (data.financial_assets.bank_accounts) {
        data.financial_assets.bank_accounts.forEach((asset, idx) => {
          assets.push({
            id: `bank_${idx}`,
            category: 'Bank Account',
            name: `${asset.bank_name || 'Bank'} - ${asset.account_number || 'Account'}`,
            type: 'financial'
          });
        });
      }
      if (data.financial_assets.fixed_deposits) {
        data.financial_assets.fixed_deposits.forEach((asset, idx) => {
          assets.push({
            id: `fd_${idx}`,
            category: 'Fixed Deposit',
            name: `${asset.bank_name || 'Bank'} - FD No. ${asset.fd_number || idx + 1}`,
            type: 'financial'
          });
        });
      }
      if (data.financial_assets.insurance) {
        data.financial_assets.insurance.forEach((asset, idx) => {
          assets.push({
            id: `insurance_${idx}`,
            category: 'Insurance',
            name: `${asset.company_name || 'Insurance'} - Policy ${asset.policy_number || idx + 1}`,
            type: 'financial'
          });
        });
      }
      if (data.financial_assets.mutual_funds) {
        data.financial_assets.mutual_funds.forEach((asset, idx) => {
          assets.push({
            id: `mf_${idx}`,
            category: 'Mutual Fund',
            name: `${asset.fund_name || 'Fund'} - Folio ${asset.folio_number || idx + 1}`,
            type: 'financial'
          });
        });
      }
      // MF clause-based entries (when using declaration clauses instead of individual entries)
      if (data.mf_clauses && (!data.financial_assets.mutual_funds || data.financial_assets.mutual_funds.length === 0)) {
        if (data.mf_clauses.sole_holder) {
          assets.push({
            id: 'mf_clause_sole',
            category: 'Mutual Fund',
            name: 'Mutual Fund Holdings — Sole Holder',
            type: 'financial'
          });
        }
        if (data.mf_clauses.primary_holder) {
          assets.push({
            id: 'mf_clause_primary',
            category: 'Mutual Fund',
            name: 'Mutual Fund Holdings — Primary/First Holder',
            type: 'financial'
          });
        }
      }
      if (data.financial_assets.shares) {
        data.financial_assets.shares.forEach((asset, idx) => {
          const broker = asset.company_name || asset.broker_name || 'Unknown Broker';
          const demat = asset.demat_account ? ` (A/c: ${asset.demat_account})` : '';
          assets.push({
            id: `share_${idx}`,
            category: 'Shares & Securities',
            name: `Demat Account — ${broker}${demat}`,
            type: 'financial'
          });
        });
      }
      if (data.financial_assets.pf_pension) {
        data.financial_assets.pf_pension.forEach((asset, idx) => {
          assets.push({
            id: `pf_${idx}`,
            category: 'PF/Pension',
            name: `${asset.type || 'PF/Pension'} - ${asset.account_number || idx + 1}`,
            type: 'financial'
          });
        });
      }
    }

    // Tangible Assets
    if (data.tangible_assets) {
      data.tangible_assets.forEach((asset, idx) => {
        if (asset.type === 'vehicle') {
          assets.push({
            id: `tangible_${idx}`,
            category: 'Vehicle',
            name: `${asset.description || 'Vehicle'} - ${asset.registration_no || 'Reg No: N/A'}`,
            type: 'tangible'
          });
        } else if (asset.type === 'jewellery' || asset.type === 'valuable' || asset.type === 'gold' || asset.type === 'silver' || asset.type === 'artwork') {
          let categoryName = 'Jewellery & Valuables';
          if (asset.type === 'gold') categoryName = 'Metals - Gold';
          else if (asset.type === 'silver') categoryName = 'Metals - Silver';
          else if (asset.type === 'artwork') categoryName = 'Artwork / Antique';
          else if (asset.type === 'valuable') categoryName = 'Other Valuable';
          else if (asset.type === 'jewellery') categoryName = 'Jewellery';
          
          assets.push({
            id: `tangible_${idx}`,
            category: categoryName,
            name: `${asset.description || 'Item'}${asset.value ? ` - Rs. ${asset.value}` : ''}`,
            type: 'tangible'
          });
        }
      });
    }

    // Intellectual Property
    if (data.intellectual_property) {
      data.intellectual_property.forEach((asset, idx) => {
        assets.push({
          id: `ip_${idx}`,
          category: 'Intellectual Property',
          name: `${asset.name || 'IP'} - ${asset.unique_identifier || 'No ID'}`,
          type: 'ip'
        });
      });
    }

    // Safety Lockers
    if (data.safety_lockers) {
      data.safety_lockers.forEach((asset, idx) => {
        assets.push({
          id: `locker_${idx}`,
          category: 'Safety Locker',
          name: `Locker ${asset.locker_number || idx + 1} at ${asset.bank_name || 'Bank'}`,
          type: 'locker'
        });
      });
    }

    // Business Interests
    if (data.business_interests) {
      data.business_interests.forEach((asset, idx) => {
        const businessType = asset.business_type === 'Others' ? asset.other_type : asset.business_type;
        assets.push({
          id: `business_${idx}`,
          category: 'Business Interest',
          name: `${asset.ownership_percentage || '0'}% in ${asset.business_name || 'Business'} (${businessType || 'N/A'})`,
          type: 'business'
        });
      });
    }

    // ESOPs / RSUs
    if (data.esops) {
      data.esops.forEach((asset, idx) => {
        assets.push({
          id: `esop_${idx}`,
          category: 'ESOP/RSU',
          name: `${asset.total_shares || '0'} shares in ${asset.firm_name || 'Company'} (Vested: ${asset.vested_esops || '0'})`,
          type: 'esop'
        });
      });
    }

    // Digital Assets
    if (data.digital_assets) {
      data.digital_assets.forEach((asset, idx) => {
        assets.push({
          id: `digital_${idx}`,
          category: 'Digital Asset',
          name: `${asset.description || 'Digital Asset'} on ${asset.platform_name || 'Platform'}`,
          type: 'digital'
        });
      });
    }

    // Foreign Assets
    if (data.foreign_assets) {
      data.foreign_assets.forEach((asset, idx) => {
        const assetName = asset.institution 
          ? `${asset.institution} (${asset.country || 'Foreign'})` 
          : `Foreign Asset in ${asset.country || 'Unknown Country'}`;
        assets.push({
          id: `foreign_${idx}`,
          category: 'Foreign Asset',
          name: `${asset.asset_type || 'Asset'} - ${assetName}`,
          type: 'foreign'
        });
      });
    }

    return assets;
  };

  const availableAssets = getAllAssets();

  // Update asset allocation percentage
  const updateAssetAllocation = (assetId, beneficiaryIndex, percentage) => {
    setFormData(prev => {
      const newAllocations = { ...prev.asset_allocations };
      if (!newAllocations[assetId]) {
        newAllocations[assetId] = {};
      }
      
      const parsedPercentage = parseFloat(percentage) || 0;
      if (parsedPercentage > 0) {
        newAllocations[assetId][beneficiaryIndex] = parsedPercentage;
      } else {
        delete newAllocations[assetId][beneficiaryIndex];
        // Clean up empty asset entries
        if (Object.keys(newAllocations[assetId]).length === 0) {
          delete newAllocations[assetId];
        }
      }
      
      return { ...prev, asset_allocations: newAllocations };
    });
  };

  // Calculate total percentage for an asset
  const getAssetTotalPercentage = (assetId) => {
    const allocations = formData.asset_allocations[assetId] || {};
    return Object.values(allocations).reduce((sum, pct) => sum + (parseFloat(pct) || 0), 0);
  };

  // Get allocation for a specific beneficiary and asset
  const getAllocation = (assetId, beneficiaryIndex) => {
    return formData.asset_allocations[assetId]?.[beneficiaryIndex] || '';
  };

  // Auto-fill remaining percentage for an asset
  const autoFillRemaining = (assetId, targetBeneficiaryIndex) => {
    const currentTotal = getAssetTotalPercentage(assetId);
    const currentAllocation = parseFloat(getAllocation(assetId, targetBeneficiaryIndex)) || 0;
    const remaining = 100 - currentTotal + currentAllocation;
    
    if (remaining > 0 && remaining <= 100) {
      updateAssetAllocation(assetId, targetBeneficiaryIndex, remaining);
    }
  };

  const addBeneficiary = () => {
    setFormData(prev => ({
      ...prev,
      beneficiaries: [...prev.beneficiaries, {
        name: '',
        relationship: '',
        share_percentage: '',
        contingent_beneficiary: ''
      }]
    }));
  };

  const updateBeneficiary = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.map((b, i) => 
        i === index ? { ...b, [field]: value } : b
      )
    }));
  };

  const removeBeneficiary = (index) => {
    setFormData(prev => {
      // Also clean up asset allocations for this beneficiary
      const newAllocations = { ...prev.asset_allocations };
      Object.keys(newAllocations).forEach(assetId => {
        delete newAllocations[assetId][index];
        // Reindex remaining beneficiaries
        const updatedAssetAlloc = {};
        Object.entries(newAllocations[assetId]).forEach(([benIdx, pct]) => {
          const numIdx = parseInt(benIdx);
          if (numIdx > index) {
            updatedAssetAlloc[numIdx - 1] = pct;
          } else {
            updatedAssetAlloc[numIdx] = pct;
          }
        });
        newAllocations[assetId] = updatedAssetAlloc;
        
        if (Object.keys(newAllocations[assetId]).length === 0) {
          delete newAllocations[assetId];
        }
      });
      
      return {
        ...prev,
        beneficiaries: prev.beneficiaries.filter((_, i) => i !== index),
        asset_allocations: newAllocations
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.beneficiaries.length === 0) {
      toast.error('Please add at least one beneficiary');
      return;
    }

    // Validate beneficiary names
    const unnamedBeneficiary = formData.beneficiaries.findIndex(b => !b.name.trim());
    if (unnamedBeneficiary !== -1) {
      toast.error(`Please enter a name for Beneficiary #${unnamedBeneficiary + 1}`);
      return;
    }

    if (formData.distribution_method === 'percentage') {
      const totalPercentage = formData.beneficiaries.reduce((sum, b) => sum + (parseFloat(b.share_percentage) || 0), 0);
      if (Math.abs(totalPercentage - 100) > 0.01) {
        toast.error('Total distribution must equal 100%');
        return;
      }
    }

    // Validate asset allocations when using specific_assets method
    if (formData.distribution_method === 'specific_assets') {
      const allAssets = getAllAssets();
      
      if (allAssets.length === 0) {
        toast.error('No assets found. Please complete Step 4 first.');
        return;
      }
      
      // Check each asset totals 100%
      const invalidAssets = [];
      allAssets.forEach(asset => {
        const total = getAssetTotalPercentage(asset.id);
        if (Math.abs(total - 100) > 0.01) {
          invalidAssets.push({ name: asset.name, total });
        }
      });
      
      if (invalidAssets.length > 0) {
        const assetList = invalidAssets.slice(0, 3).map(a => `• ${a.name}: ${a.total.toFixed(1)}%`).join('\n');
        const remaining = invalidAssets.length > 3 ? `\n...and ${invalidAssets.length - 3} more` : '';
        toast.error(`Each asset must total 100%. Issues found:\n${assetList}${remaining}`);
        return;
      }
    }

    onNext(formData);
  };

  const totalPercentage = formData.beneficiaries.reduce((sum, b) => sum + (parseFloat(b.share_percentage) || 0), 0);

  // Build family members list for quick select
  const getFamilyMembers = () => {
    const members = [];
    
    if (data.spouse_details?.name) {
      members.push({ name: data.spouse_details.name, relationship: 'Spouse' });
    }
    
    if (data.children) {
      data.children.forEach(child => {
        if (child.name) {
          members.push({ name: child.name, relationship: 'Child' });
        }
      });
    }
    
    if (data.father_name) {
      members.push({ name: data.father_name, relationship: 'Father' });
    }
    
    if (data.mother_name) {
      members.push({ name: data.mother_name, relationship: 'Mother' });
    }
    
    if (data.parents) {
      data.parents.forEach(parent => {
        if (parent.name && parent.name !== data.father_name && parent.name !== data.mother_name) {
          members.push({ name: parent.name, relationship: parent.relation });
        }
      });
    }
    
    return members;
  };

  const familyMembers = getFamilyMembers();

  // Group assets by category for better display
  const getAssetsByCategory = () => {
    const grouped = {};
    availableAssets.forEach(asset => {
      if (!grouped[asset.category]) {
        grouped[asset.category] = [];
      }
      grouped[asset.category].push(asset);
    });
    return grouped;
  };

  const assetsByCategory = getAssetsByCategory();

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step5-beneficiaries-form">
      {/* Distribution Method Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
          <Users className="text-amber-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Distribution Method</h3>
        </div>
        
        <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-700/50 transition-colors">
            <input 
              type="radio" 
              name="distribution_method"
              value="single" 
              checked={formData.distribution_method === 'single'} 
              onChange={(e) => setFormData(prev => ({ ...prev, distribution_method: e.target.value }))}
              className="w-4 h-4 text-amber-500 bg-slate-700 border-slate-600 focus:ring-amber-500"
            />
            <div>
              <span className="text-white font-medium">Leave everything to one beneficiary</span>
              <p className="text-slate-400 text-sm">All your assets will go to a single person</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-700/50 transition-colors">
            <input 
              type="radio" 
              name="distribution_method"
              value="percentage" 
              checked={formData.distribution_method === 'percentage'} 
              onChange={(e) => setFormData(prev => ({ ...prev, distribution_method: e.target.value }))}
              className="w-4 h-4 text-amber-500 bg-slate-700 border-slate-600 focus:ring-amber-500"
            />
            <div>
              <span className="text-white font-medium">Divide by percentage among beneficiaries</span>
              <p className="text-slate-400 text-sm">Split your entire estate by percentages (must total 100%)</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-700/50 transition-colors">
            <input 
              type="radio" 
              name="distribution_method"
              value="specific_assets" 
              checked={formData.distribution_method === 'specific_assets'} 
              onChange={(e) => setFormData(prev => ({ ...prev, distribution_method: e.target.value }))}
              className="w-4 h-4 text-amber-500 bg-slate-700 border-slate-600 focus:ring-amber-500"
            />
            <div>
              <span className="text-white font-medium">Assign specific assets with percentages</span>
              <p className="text-slate-400 text-sm">Allocate each asset by percentage to beneficiaries (each asset must total 100%)</p>
            </div>
          </label>
        </div>
      </div>

      {/* Beneficiaries Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
          <Users className="text-amber-400" size={20} />
          <h3 className="text-lg font-semibold text-white">Beneficiaries</h3>
          <FieldTooltip content="Beneficiaries can be individuals, minors, charities, or even trusts. Think carefully about whom you want to inherit your assets." />
        </div>
        <p className="text-sm text-slate-400">People who will inherit your estate</p>

        {formData.beneficiaries.map((beneficiary, index) => (
          <div 
            key={index} 
            className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-5"
            data-testid={`beneficiary-card-${index}`}
          >
            <div className="flex justify-between items-center">
              <h4 className="text-amber-400 font-semibold text-lg">Beneficiary #{index + 1}</h4>
              {formData.beneficiaries.length > 1 && (
                <button 
                  type="button" 
                  onClick={() => removeBeneficiary(index)} 
                  className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm transition-colors"
                >
                  <Trash2 size={16} /> Remove
                </button>
              )}
            </div>
            
            {/* Quick Select from Family */}
            {familyMembers.length > 0 && (
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-600">
                <Label className="text-amber-400 font-semibold mb-2 block">Quick Select from Family</Label>
                <select 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && value !== 'new') {
                      try {
                        const selectedData = JSON.parse(value);
                        updateBeneficiary(index, 'name', selectedData.name);
                        updateBeneficiary(index, 'relationship', selectedData.relationship);
                      } catch (err) {
                        console.error('Error parsing family member data:', err);
                      }
                    }
                    e.target.value = '';
                  }}
                  className={selectClass}
                  data-testid={`quick-select-${index}`}
                >
                  <option value="">-- Select from family or add manually below --</option>
                  {familyMembers.map((member, idx) => (
                    <option 
                      key={idx}
                      value={JSON.stringify({ name: member.name, relationship: member.relationship })}
                    >
                      {member.name} ({member.relationship})
                    </option>
                  ))}
                  <option disabled>──────────</option>
                  <option value="new">✏️ Add Someone Else Manually</option>
                </select>
              </div>
            )}
            
            {/* Name and Relationship */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Full Name *</Label>
                <Input 
                  type="text" 
                  value={beneficiary.name} 
                  onChange={(e) => updateBeneficiary(index, 'name', e.target.value)} 
                  required 
                  className={inputClass}
                  data-testid={`beneficiary-name-${index}`}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Relationship *</Label>
                <select 
                  value={beneficiary.relationship} 
                  onChange={(e) => updateBeneficiary(index, 'relationship', e.target.value)} 
                  required
                  className={selectClass}
                  data-testid={`beneficiary-relationship-${index}`}
                >
                  <option value="">Select</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Son">Son</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Brother">Brother</option>
                  <option value="Sister">Sister</option>
                  <option value="Grandson">Grandson</option>
                  <option value="Granddaughter">Granddaughter</option>
                  <option value="Nephew">Nephew</option>
                  <option value="Niece">Niece</option>
                  <option value="Uncle">Uncle</option>
                  <option value="Aunt">Aunt</option>
                  <option value="Cousin">Cousin</option>
                  <option value="Partner">Partner</option>
                  <option value="Friend">Friend</option>
                  <option value="Charity/Organization">Charity/Organization</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Share Percentage - Only for percentage method */}
            {formData.distribution_method === 'percentage' && (
              <div className="space-y-2">
                <Label className={labelClass}>Share Percentage * (Must total 100%)</Label>
                <div className="flex items-center gap-3">
                  <Input 
                    type="number" 
                    value={beneficiary.share_percentage} 
                    onChange={(e) => updateBeneficiary(index, 'share_percentage', e.target.value)} 
                    min="0" 
                    max="100" 
                    step="0.01"
                    required 
                    className={`${inputClass} w-32`}
                    data-testid={`beneficiary-percentage-${index}`}
                  />
                  <span className="text-amber-400 font-semibold">%</span>
                </div>
              </div>
            )}

            {/* Contingent Beneficiary */}
            <div className="space-y-2">
              <Label className={labelClass}>Contingent Beneficiary</Label>
              <Input 
                type="text" 
                value={beneficiary.contingent_beneficiary || ''} 
                onChange={(e) => updateBeneficiary(index, 'contingent_beneficiary', e.target.value)} 
                placeholder="If this beneficiary predeceases you, who should receive their share?"
                className={inputClass}
                data-testid={`beneficiary-contingent-${index}`}
              />
              <p className="text-xs text-slate-500">Optional: Name someone to inherit if this beneficiary passes away before you</p>
            </div>
          </div>
        ))}

        {/* Add Beneficiary Button - Hidden for single distribution with one beneficiary */}
        {(formData.distribution_method !== 'single' || formData.beneficiaries.length === 0) && (
          <Button 
            type="button" 
            onClick={addBeneficiary} 
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            data-testid="add-beneficiary-btn"
          >
            <Plus size={18} className="mr-2" /> Add Beneficiary
          </Button>
        )}

        {/* Single Distribution Info */}
        {formData.distribution_method === 'single' && formData.beneficiaries.length >= 1 && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-blue-400 font-medium flex items-center gap-2">
              <AlertCircle size={18} />
              You have selected &ldquo;Leave everything to one beneficiary&rdquo;. Only one beneficiary can be added.
            </p>
          </div>
        )}

        {/* Percentage Summary */}
        {formData.distribution_method === 'percentage' && formData.beneficiaries.length > 0 && (
          <div className={`p-4 rounded-lg border ${
            Math.abs(totalPercentage - 100) < 0.01 
              ? 'bg-emerald-500/10 border-emerald-500' 
              : 'bg-amber-500/10 border-amber-500'
          }`}>
            <p className={`font-semibold ${
              Math.abs(totalPercentage - 100) < 0.01 ? 'text-emerald-400' : 'text-amber-400'
            }`}>
              Total Distribution: {totalPercentage.toFixed(2)}%
              {Math.abs(totalPercentage - 100) < 0.01 && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <Check size={16} /> Complete
                </span>
              )}
            </p>
            {Math.abs(totalPercentage - 100) >= 0.01 && (
              <p className="text-sm text-amber-300 mt-1">
                Must equal exactly 100%
                {totalPercentage < 100 
                  ? ` (Need ${(100 - totalPercentage).toFixed(2)}% more)`
                  : ` (Exceeds by ${(totalPercentage - 100).toFixed(2)}%)`
                }
              </p>
            )}
          </div>
        )}
      </div>

      {/* Asset Allocation Section - Only for specific_assets method */}
      {formData.distribution_method === 'specific_assets' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
            <PieChart className="text-amber-400" size={20} />
            <h3 className="text-lg font-semibold text-white">Asset Allocation</h3>
          </div>
          <p className="text-sm text-slate-400">
            Assign percentage shares of each asset to your beneficiaries. Each asset must total exactly 100%.
          </p>

          {availableAssets.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="text-red-400" size={18} />
              <p className="text-red-400 text-sm">
                No assets found. Please complete Step 4 (Define Assets) first.
              </p>
            </div>
          ) : formData.beneficiaries.length === 0 || !formData.beneficiaries.some(b => b.name.trim()) ? (
            <div className="flex items-center gap-2 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <AlertCircle className="text-amber-400" size={18} />
              <p className="text-amber-400 text-sm">
                Please add beneficiaries with names above before allocating assets.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(assetsByCategory).map(([category, assets]) => (
                <div key={category} className="space-y-3">
                  <h4 className="text-amber-400 font-semibold text-base bg-slate-800/80 px-3 py-2 rounded-lg">
                    {category}
                  </h4>
                  
                  {assets.map((asset) => {
                    const assetTotal = getAssetTotalPercentage(asset.id);
                    const isComplete = Math.abs(assetTotal - 100) < 0.01;
                    
                    return (
                      <div 
                        key={asset.id} 
                        className={`bg-slate-800/50 p-4 rounded-lg border transition-colors ${
                          isComplete ? 'border-emerald-500/50' : 'border-slate-700'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <p className="text-white font-medium">{asset.name}</p>
                          </div>
                          <div className={`text-sm font-semibold px-2 py-1 rounded ${
                            isComplete 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : assetTotal > 100 
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {assetTotal.toFixed(1)}% / 100%
                            {isComplete && <Check size={14} className="inline ml-1" />}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {formData.beneficiaries.map((ben, benIdx) => {
                            if (!ben.name.trim()) return null;
                            const allocation = getAllocation(asset.id, benIdx);
                            
                            return (
                              <div key={benIdx} className="flex items-center gap-2 bg-slate-900/50 p-2 rounded-lg">
                                <div className="flex-1 min-w-0">
                                  <p className="text-slate-300 text-sm truncate" title={ben.name}>
                                    {ben.name}
                                  </p>
                                  <p className="text-slate-500 text-xs">{ben.relationship || 'N/A'}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={allocation}
                                    onChange={(e) => updateAssetAllocation(asset.id, benIdx, e.target.value)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    placeholder="0"
                                    className={`${inputClass} w-20 text-center text-sm`}
                                    data-testid={`allocation-${asset.id}-${benIdx}`}
                                  />
                                  <span className="text-slate-400 text-sm">%</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => autoFillRemaining(asset.id, benIdx)}
                                  className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 transition-colors"
                                  title="Fill remaining percentage"
                                >
                                  Fill
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        
                        {!isComplete && (
                          <p className="text-xs text-slate-500 mt-2">
                            {assetTotal < 100 
                              ? `${(100 - assetTotal).toFixed(1)}% remaining to allocate`
                              : `Exceeds 100% by ${(assetTotal - 100).toFixed(1)}%`
                            }
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Overall Asset Allocation Summary */}
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <h4 className="text-white font-semibold mb-3">Allocation Summary</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {availableAssets.map(asset => {
                    const total = getAssetTotalPercentage(asset.id);
                    const isComplete = Math.abs(total - 100) < 0.01;
                    return (
                      <div 
                        key={asset.id}
                        className={`p-2 rounded text-xs ${
                          isComplete 
                            ? 'bg-emerald-500/10 text-emerald-400' 
                            : total > 100
                              ? 'bg-red-500/10 text-red-400'
                              : 'bg-amber-500/10 text-amber-400'
                        }`}
                      >
                        <p className="truncate font-medium" title={asset.name}>{asset.name}</p>
                        <p>{total.toFixed(1)}%</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button 
            type="button" 
            onClick={onPrevious} 
            variant="outline" 
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button 
            type="button" 
            onClick={() => onSaveAndExit(formData)} 
            disabled={saving} 
            variant="outline" 
            className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button 
          type="submit" 
          disabled={saving} 
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
        >
          Next: Exclusions <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step5Beneficiaries;
